import { useState, useEffect, useMemo } from "react";
import countryList from "react-select-country-list";
import { defaultUrl } from "../../features/actions";
import { makeFetchRequest } from "../../utilities/common";
import {
  User,
  GraduationCap,
  CalendarDays,
  MapPin,
  ChevronRight,
  CheckCircle2,
  Info,
} from "lucide-react";

const PRIMARY = "#0F2A6B";

export const First = ({ handleChange, nextStep, form }: any) => {
  const [getClass, setGetClass] = useState([]);

  useEffect(() => {
    async function queryClassrooms() {
      const data = await makeFetchRequest(
        `${defaultUrl}/classroom`,
        false,
        true,
      );
      setGetClass(data);
    }
    queryClassrooms();
  }, []);

  const options = useMemo(() => countryList().getData(), []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col md:flex-row">
      {/* --- Left Sidebar --- */}

      {/* --- Main Content --- */}
      <div className="flex-1 overflow-y-auto">
        <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-slate-100 px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-400">
              Step 01/03
            </span>
            <div className="h-1 w-20 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-600 w-1/3"></div>
            </div>
          </div>
          <></>
        </header>

        <main className="max-w-4xl p-5 lg:p-5 mx-auto">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              nextStep();
            }}
            className="space-y-12"
          >
            {/* Section 1: Basic Bio */}
            <section>
              <div className="flex items-center gap-3 mb-8">
                <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
                  <User size={20} />
                </div>
                <h2 className="text-xl font-bold text-slate-800">
                  Student Identity
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <FloatingInput
                  name="firstname"
                  label="First Name"
                  required
                  form={form}
                  onChange={handleChange}
                />
                <FloatingInput
                  name="surname"
                  label="Surname"
                  required
                  form={form}
                  onChange={handleChange}
                />
                <FloatingInput
                  name="othername"
                  label="Other Names"
                  form={form}
                  onChange={handleChange}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                <GenderSelector form={form} handleChange={handleChange} />
                <FloatingInput
                  type="date"
                  name="date_of_birth"
                  label="Date of Birth"
                  required
                  form={form}
                  onChange={handleChange}
                />
              </div>
            </section>

            {/* Section 2: Academic Placement */}
            <section className="pt-8 border-t border-slate-100">
              <div className="flex items-center gap-3 mb-8">
                <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
                  <GraduationCap size={20} />
                </div>
                <h2 className="text-xl font-bold text-slate-800">
                  Academic Placement
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="relative group">
                  <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 block ml-1">
                    Class Enrollment *
                  </label>
                  <select
                    name="classroom_id"
                    value={form.classroom_id}
                    onChange={({ target }) => handleChange(target)}
                    className="modern-select"
                    required
                  >
                    <option value="">Select a classroom</option>
                    {getClass.map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>

                <FloatingInput
                  type="date"
                  name="admission_date"
                  label="Admission Date"
                  required
                  form={form}
                  onChange={handleChange}
                />
              </div>
            </section>

            {/* Section 3: Contact */}
            <section className="pt-8 border-t border-slate-100">
              <div className="flex items-center gap-3 mb-8">
                <div className="p-2 bg-orange-50 text-orange-600 rounded-lg">
                  <MapPin size={20} />
                </div>
                <h2 className="text-xl font-bold text-slate-800">
                  Residential Address
                </h2>
              </div>

              <div className="space-y-6">
                <FloatingInput
                  name="house_address"
                  label="Street Address"
                  required
                  form={form}
                  onChange={handleChange}
                />

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <FloatingInput
                    name="city"
                    label="City"
                    required
                    form={form}
                    onChange={handleChange}
                  />
                  <FloatingInput
                    name="state"
                    label="State"
                    required
                    form={form}
                    onChange={handleChange}
                  />
                  <div className="col-span-2">
                    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 block ml-1">
                      Country *
                    </label>
                    <select
                      name="country"
                      value={form.country}
                      onChange={({ target }) => handleChange(target)}
                      className="modern-select"
                      required
                    >
                      <option value="">Select Country</option>
                      <option value="Nigeria">Nigeria</option>
                      {options.map((c: any) => (
                        <option key={c.label} value={c.label}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </section>

            {/* Footer */}
            <div className="pt-10 flex items-center justify-between border-t border-slate-200">
              <p className="text-sm text-slate-400 font-medium">Step 1 of 3</p>
              <button
                type="submit"
                className="group flex items-center gap-2 px-8 py-4 bg-slate-900 text-white rounded-2xl font-bold hover:bg-black transition-all shadow-xl hover:shadow-2xl hover:-translate-y-1 active:translate-y-0"
              >
                Continue to Medical Details
                <ChevronRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </button>
            </div>
          </form>
        </main>
      </div>
    </div>
  );
};

/* --- Sub-Components --- */

const StepItem = ({ icon, label, active }: any) => (
  <div
    className={`flex items-center gap-4 transition-colors ${active ? "text-slate-900" : "text-slate-400"}`}
  >
    <div
      className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${active ? "bg-slate-900 text-white shadow-lg shadow-slate-200" : "bg-slate-50"}`}
    >
      {icon}
    </div>
    <span
      className={`text-sm font-bold ${active ? "opacity-100" : "opacity-60"}`}
    >
      {label}
    </span>
  </div>
);

const FloatingInput = ({
  name,
  label,
  form,
  onChange,
  type = "text",
  required,
}: any) => (
  <div className="relative">
    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 block ml-1">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <input
      type={type}
      name={name}
      value={form[name] || ""}
      onChange={({ target }) => onChange(target)}
      required={required}
      className="modern-input"
    />
  </div>
);

// --- FIXED GENDER SELECTOR ---
const GenderSelector = ({ form, handleChange }: any) => (
  <div className="w-full">
    <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2 block ml-1">
      Gender *
    </label>
    <div className="flex p-1.5 bg-slate-100 rounded-xl">
      {["male", "female"].map((g) => (
        <button
          key={g}
          type="button" // Important: prevents form submission
          onClick={() => handleChange({ name: "gender", value: g })}
          className={`flex-1 py-3 text-sm font-bold rounded-lg transition-all duration-200 ${
            form.gender === g
              ? "bg-white text-slate-900 shadow-sm"
              : "text-slate-500 hover:text-slate-700"
          }`}
        >
          {g === "male" ? "Boy" : "Girl"}
        </button>
      ))}
    </div>
  </div>
);

const styles = `
  .modern-input {
    width: 100%;
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    padding: 14px 16px;
    border-radius: 14px;
    font-size: 15px;
    font-weight: 500;
    color: #1E293B;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .modern-input:focus {
    outline: none;
    border-color: #0F2A6B;
    box-shadow: 0 0 0 4px rgba(15, 42, 107, 0.08);
  }
  .modern-select {
    width: 100%;
    background: #FFFFFF;
    border: 1px solid #E2E8F0;
    padding: 14px 16px;
    border-radius: 14px;
    font-size: 14px;
    font-weight: 500;
    color: #1E293B;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 1rem center;
    background-size: 1.2em;
  }
`;

if (typeof document !== "undefined") {
  const s = document.createElement("style");
  s.innerHTML = styles;
  document.head.appendChild(s);
}
